/**
 * Webapp component.
 *
 * Copyright © Ircam Amplify 2024-2026
 */
import amplifyConfig from '@/amplify.config'
import MainContainer from '@/components/Maincontainer'
import MyAccount from '@/components/MyAccount'
import SideBar from '@/components/Sidebar'
import TopBar from '@/components/TopBar'
import TwoPaneLayout from '@/components/TwoPaneLayout'
import RequiresAuth from '@/guards/RequiresAuth'
import RequiresNoAuth from '@/guards/RequiresNoAuth'
import '@fontsource/barlow/400.css'
import '@fontsource/barlow/500.css'
import '@fontsource/barlow/600.css'

import {
  Billing,
  ChangePassword,
  CheckoutPage,
  ConfirmAccount,
  CreateWebhookPage,
  ForgotPassword,
  ForgotPasswordConfirmation,
  HomePage,
  Login,
  NewApiCredentials,
  ResendConfirmationEmail,
  SelectTier,
  Settings,
  Signup,
  TaskCreationModuleSelectionPage,
  TaskCreationPage,
  TaskListPage,
  TeamPage,
} from '@/pages'
import { CheckoutResultPage } from '@/pages/Checkout/CheckoutResultPage'
import {
  getUserState,
  persistor,
  setSideBarCollapsed,
  store,
  useSelector,
} from '@/store'
import { Amplify } from 'aws-amplify'
import { useEffect } from 'react'
import 'react-loading-skeleton/dist/skeleton.css'
import { Provider } from 'react-redux'
import { Outlet, Route, Routes } from 'react-router'
import { useLocation } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import { AppRouteUrl, getRouteTitle } from './AppRoute'
import ApiKeyWebhookPage from './pages/ApiKeyWebhook/ApiKeyWebookPage'

Amplify.configure(amplifyConfig)
console.log(`App version: ${import.meta.env.VITE_APP_VERSION}`)
console.log('Locale:', navigator.language)

const HomeLayout = () => {
  const { userInfo } = useSelector(getUserState)
  return (
    <>
      <TopBar
        title={`Welcome ${userInfo?.value?.user_firstname ?? ''}`}
        description=""
      />
      <HomePage />
    </>
  )
}

const App = () => {
  const location = useLocation()

  useEffect(() => {
    const routeMsg = getRouteTitle(location?.pathname)
    if (routeMsg && document.title !== routeMsg) {
      document.title = `${routeMsg} | ${window.location.host}`
    }
  }, [location?.pathname])

  useEffect(() => {
    const handleResize = () => {
      const width =
        window?.innerWidth ||
        document?.documentElement?.clientWidth ||
        document?.body?.clientWidth ||
        0
      const sideBarCollapsed = store.getState().layout.sideBarCollapsed
      if (width < 640 && !sideBarCollapsed) {
        store.dispatch(setSideBarCollapsed(true))
      }
      if (width >= 640 && sideBarCollapsed) {
        store.dispatch(setSideBarCollapsed(false))
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.addEventListener('resize', handleResize)
    }
  }, [])

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Routes>
          <Route
            path="/login"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <Login />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="/confirm"
            element={
              <RequiresNoAuth>
                <ConfirmAccount />
              </RequiresNoAuth>
            }
          />
          <Route
            path="/signup"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <Signup />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <ForgotPassword />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="/forgot-password-confirmation"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <ForgotPasswordConfirmation />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="/resend-activation-link"
            element={
              <RequiresNoAuth>
                <TwoPaneLayout>
                  <ResendConfirmationEmail />
                </TwoPaneLayout>
              </RequiresNoAuth>
            }
          />
          <Route
            path="tierselection"
            element={
              <RequiresAuth>
                <SelectTier />
              </RequiresAuth>
            }
          />

          <Route
            path="/change-password"
            element={
              <RequiresAuth>
                <TwoPaneLayout>
                  <ChangePassword />
                </TwoPaneLayout>
              </RequiresAuth>
            }
          />

          <Route
            path="checkout"
            element={
              <RequiresAuth>
                <CheckoutPage />
              </RequiresAuth>
            }
          />
          <Route
            path="payment-result"
            element={
              <RequiresAuth>
                <CheckoutResultPage />
              </RequiresAuth>
            }
          />
          <Route
            path="/"
            element={
              <RequiresAuth>
                <>
                  <SideBar />
                  <MainContainer>
                    <Outlet />
                  </MainContainer>
                </>
              </RequiresAuth>
            }
          >
            <Route path="" element={<HomeLayout />} />
            <Route
              path="settings"
              element={
                <>
                  <TopBar title="Settings" description="Set your alerts" />
                  <Settings />
                </>
              }
            />
            <Route
              path="usage"
              element={
                <>
                  <TopBar title={'Usage'} description="" />
                  <Billing />
                </>
              }
            />
            <Route
              path="team"
              element={
                <>
                  <TopBar
                    title={'Team'}
                    description="Manage access to your workspace"
                  />
                  <TeamPage />
                </>
              }
            />
            <Route
              path={AppRouteUrl.ApiKeyCreate}
              element={
                <>
                  <TopBar title="Create an API key" />
                  <NewApiCredentials />
                </>
              }
            />
            <Route path={AppRouteUrl.ApiKey} element={<ApiKeyWebhookPage />} />
            <Route
              path="my-account"
              element={
                <>
                  <MyAccount />
                </>
              }
            />
            <Route
              path="send-activation-link"
              element={
                <TwoPaneLayout>
                  <ResendConfirmationEmail />
                </TwoPaneLayout>
              }
            />
            <Route
              path="jobs"
              element={
                <>
                  <TopBar title="7-Days Task History" description="" />
                  <TaskListPage />
                </>
              }
            />
            <Route
              path={AppRouteUrl.TaskCreation}
              element={<TaskCreationModuleSelectionPage />}
            />
            <Route
              path="job-creation/:moduleName"
              element={
                <>
                  <TaskCreationPage />
                </>
              }
            />
            <Route
              path={AppRouteUrl.WebhookCreate}
              element={
                <>
                  <TopBar title="Create a Webhook" />
                  <CreateWebhookPage />
                </>
              }
            />
            <Route
              path="*"
              element={<div>The page you are looking for does not exist.</div>}
            />
          </Route>
        </Routes>
      </PersistGate>
    </Provider>
  )
}

export default App
